/* eslint-disable @typescript-eslint/no-floating-promises */
import { Button } from '@/components/atoms/Button/Button'
import { useFormik } from 'formik'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import * as Yup from 'yup'
import i18n from '@/i18n/i18n'

// Definición del tipo de los valores del formulario
interface FormValues {
  fullName: string
  email: string
  phoneNumber: string
  interestedIn: string
  existingCompanyName: string
  countryOfCitizenship: string
  message: string
  subject: string
}

export const ContactUsFormModal: React.FC = () => {
  const language = i18n.language
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const clientId = import.meta.env.VITE_CLIENT_ID
  const apiUrl = import.meta.env.VITE_API_BASE_URL as string
  const TOAST_DURATION = 15000 // 15 segundos

  // Esquema de validación con Yup utilizando traducciones
  const validationSchema = Yup.object({
    fullName: Yup.string().required(t('formContact.validation.fullNameRequired')),
    email: Yup.string()
      .email(t('formContact.validation.emailInvalid'))
      .required(t('formContact.validation.emailRequired')),
    phoneNumber: Yup.string().required(t('formContact.validation.phoneNumberRequired')),
    interestedIn: Yup.string().required(t('formContact.validation.interestedInRequired')),
    existingCompanyName: Yup.string().required(
      t('formContact.validation.existingCompanyNameRequired')
    ),
    countryOfCitizenship: Yup.string().required(
      t('formContact.validation.countryOfCitizenshipRequired')
    ),
    message: Yup.string().required(t('formContact.validation.messageRequired'))
  })

  // Función para enviar los datos al backend
  const sendFormData = async (values: FormValues, resetForm: () => void): Promise<void> => {
    setIsLoading(true)

    try {
      const req = await fetch(`${apiUrl}/api/contact/new/${clientId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': language
        },
        body: JSON.stringify({
          full_name: values.fullName,
          email: values.email,
          phone_number: values.phoneNumber,
          interested: values.interestedIn,
          company_name: values.existingCompanyName,
          country_citizenship: values.countryOfCitizenship,
          message: values.message,
          subject: values.subject
        })
      })

      const response = await req.json()

      //console.log(response)

      if (response.success) {
        toast.success(t('formContact.messageSend'), { duration: TOAST_DURATION })
        resetForm()
      } else {
        const errors = response.errors
        errors.map((item: any) => {
          toast.error(item.message), { duration: TOAST_DURATION }
        })
      }
    } catch (error) {
      toast.error(t('formContact.messageSendError'))
    } finally {
      setIsLoading(false)
    }
  }

  // Configuración de Formik
  const formik = useFormik<FormValues>({
    initialValues: {
      fullName: '',
      email: '',
      phoneNumber: '',
      interestedIn: '',
      existingCompanyName: '',
      countryOfCitizenship: '',
      message: '',
      subject: 'Contact Website'
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      sendFormData(values, resetForm)
    }
  })

  return (
    <div>
      <div className="">
        <form
          onSubmit={formik.handleSubmit}
          className="w-full mx-auto flex-[4] bg-white rounded-[30px] mt-8"
        >
          <div className="w-full flex flex-col gap-2 mt-8 lg:min-w-[700px]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="w-full mb-4">
                <input
                  type="text"
                  name="fullName"
                  placeholder={t('formContact.fullNamePlaceholder')}
                  value={formik.values.fullName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`text-primary mt-1 block w-full p-2 border rounded ${
                    formik.touched.fullName !== undefined && formik.errors.fullName !== undefined
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                />
                {formik.touched.fullName !== undefined && formik.errors.fullName !== undefined && (
                  <div className="text-red-500 text-sm">{formik.errors.fullName}</div>
                )}
              </div>

              <div className="w-full mb-4">
                <input
                  type="email"
                  name="email"
                  placeholder={t('formContact.emailPlaceholder')}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`text-primary mt-1 block w-full p-2 border rounded ${
                    formik.touched.email !== undefined && formik.errors.email !== undefined
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                />
                {formik.touched.email !== undefined && formik.errors.email !== undefined && (
                  <div className="text-red-500 text-sm">{formik.errors.email}</div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="w-full mb-4">
                <input
                  type="text"
                  name="phoneNumber"
                  placeholder={t('formContact.phoneNumberPlaceholder')}
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`text-primary mt-1 block w-full p-2 border rounded ${
                    formik.touched.phoneNumber !== undefined &&
                    formik.errors.phoneNumber !== undefined
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                />
                {formik.touched.phoneNumber !== undefined &&
                  formik.errors.phoneNumber !== undefined && (
                    <div className="text-red-500 text-sm">{formik.errors.phoneNumber}</div>
                  )}
              </div>

              <div className="w-full mb-4">
                <input
                  type="text"
                  name="interestedIn"
                  placeholder={t('formContact.interestedIn')}
                  value={formik.values.interestedIn}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`text-primary mt-1 block w-full p-2 border rounded ${
                    formik.touched.interestedIn !== undefined &&
                    formik.errors.interestedIn !== undefined
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                />
                {formik.touched.interestedIn !== undefined &&
                  formik.errors.interestedIn !== undefined && (
                    <div className="text-red-500 text-sm">{formik.errors.interestedIn}</div>
                  )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="w-full mb-4">
                <input
                  type="text"
                  name="existingCompanyName"
                  placeholder={t('formContact.existingCompanyNamePlaceholder')}
                  value={formik.values.existingCompanyName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`text-primary mt-1 block w-full p-2 border rounded ${
                    formik.touched.existingCompanyName !== undefined &&
                    formik.errors.existingCompanyName !== undefined
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                />
                {formik.touched.existingCompanyName !== undefined &&
                  formik.errors.existingCompanyName !== undefined && (
                    <div className="text-red-500 text-sm">{formik.errors.existingCompanyName}</div>
                  )}
              </div>

              <div className="w-full mb-4">
                <input
                  type="text"
                  name="countryOfCitizenship"
                  placeholder={t('formContact.countryOfCitizenshipPlaceholder')}
                  value={formik.values.countryOfCitizenship}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`text-primary mt-1 block w-full p-2 border rounded ${
                    formik.touched.countryOfCitizenship !== undefined &&
                    formik.errors.countryOfCitizenship !== undefined
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                />
                {formik.touched.countryOfCitizenship !== undefined &&
                  formik.errors.countryOfCitizenship !== undefined && (
                    <div className="text-red-500 text-sm">{formik.errors.countryOfCitizenship}</div>
                  )}
              </div>
            </div>

            <div className="w-full mb-4">
              <textarea
                name="message"
                placeholder={t('formContact.messagePlaceholder')}
                value={formik.values.message}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`text-primary mt-1 block w-full p-2 border rounded ${
                  formik.touched.message !== undefined && formik.errors.message !== undefined
                    ? 'border-red-500'
                    : 'border-gray-300'
                }`}
              />
              {formik.touched.message !== undefined && formik.errors.message !== undefined && (
                <div className="text-red-500 text-sm">{formik.errors.message}</div>
              )}
            </div>
          </div>
          <div className="w-full flex justify-start mt-4">
            <Button
              type="submit"
              className="flex justify-center w-auto text-center py-2 px-8 font-semibold hover:bg-primary-950 transition rounded-full"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : t('formContact.submitButton')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
